import React, { useState, useEffect } from "react";
import { CircularProgress, Box, TextField } from "@mui/material";
import Swal from "sweetalert2";
import { generateAndExecuteMutation } from "../../../data/Axios/DynamicService";
import { HRService } from "../../../config";
import DynamicForm from "../../../data/Axios/DynamicForm";
import { executeAnyMutation } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { AlignVerticalJustifyStartIcon } from "lucide-react";

const base_url = HRService.uri;

/* -------------------------------------------------
   Form fields - only what user sees
   ------------------------------------------------- */
const payrollRunFields = [
  {
    name: "payrollMonth",
    label: "Payroll Month",
    type: "month", // Native HTML month picker (YYYY-MM)
    required: true,
  },
  {
    name: "payDate",
    label: "Pay Date",
    type: "date",
    required: true,
  },
  {
    name: "runType",
    label: "Run Type",
    type: "select",
    required: true,
    options: [],
  },
];

/* -------------------------------------------------
   Component
   ------------------------------------------------- */
const PayrollRunCreationForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id, 10);
  const companyId = parseInt(decodedToken.CompanyId, 10);

  const [loading, setLoading] = useState(true);
  const [runTypeOptions, setRunTypeOptions] = useState([]);

  // Static run types
  useEffect(() => {
    const opts = [
      { value: "Regular", label: "Regular" },
      { value: "Bonus", label: "Bonus" },
      { value: "Termination", label: "Termination" },
      { value: "Adjustment", label: "Adjustment" },
    ];
    setRunTypeOptions(opts);
    setLoading(false);
  }, []);

  // Helper: Convert "2025-11" → start and end dates
  const getPeriodFromMonth = (monthValue) => {
    if (!monthValue || !monthValue.includes("-")) {
      return { start: "", end: "" };
    }

    const [yearStr, monthStr] = monthValue.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10); // 1-12 (from input type="month")

    // First day: always 01
    const start = `${year}-${String(month).padStart(2, "0")}-01`;

    // Last day: use next month's 0th day
    const lastDayDate = new Date(year, month, 0); // month is 1-12 → correct!
    const lastDay = lastDayDate.getDate();
    const end = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

    return { start, end };
  };

  // For edit mode: extract YYYY-MM from periodStart
  const getInitialMonth = () => {
    if (props.data?.periodStart) {
      const date = new Date(props.data.periodStart);
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // JS is 0-based
      return `${year}-${String(month).padStart(2, "0")}`;
    }
    return "";
  };

  const initialValues = {
    payrollMonth: getInitialMonth(),
    periodStart: props.data?.periodStart || "",
    periodEnd: props.data?.periodEnd || "",
    payDate: props.data?.payDate || "",
    runType: props.data?.runType || "",
  };

  // Custom render for month picker + auto-fill logic
  const renderMonthInput = ({ field, form }) => {
    const handleMonthChange = (e) => {
      const value = e.target.value; // e.g. "2025-11"
      form.setFieldValue("payrollMonth", value);

      const { start, end } = getPeriodFromMonth(value);
      form.setFieldValue("periodStart", start);
      form.setFieldValue("periodEnd", end);
    };

    return (
      <TextField
        {...field}
        label="Payroll Month"
        type="month"
        fullWidth
        InputLabelProps={{ shrink: true }}
        onChange={handleMonthChange}
        value={field.value || ""}
        error={Boolean(form.touched.payrollMonth && form.errors.payrollMonth)}
        helperText={form.touched.payrollMonth && form.errors.payrollMonth}
      />
    );
  };

  // Populate fields with options and custom renderer
  const populatedFields = payrollRunFields.map((f) => {
    if (f.name === "runType") {
      return { ...f, options: runTypeOptions };
    }
    if (f.name === "payrollMonth") {
      return { ...f, render: renderMonthInput };
    }
    return f;
  });

  // Submit
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Re-calculate periodStart/End in case user didn't change month
      const { start, end } = getPeriodFromMonth(values.payrollMonth);

      const mutationInput = {
        companyId,
        periodStart: start || values.periodStart,
        periodEnd: end || values.periodEnd,
        payDate: values.payDate,
        runType: values.runType,
        createdBy: userId,
      };

      if (props.isEditing && props.data?.id) {
        mutationInput.id = props.data.id;
      }

      const mutationName = props.isEditing
        ? "updatePayrollRun"
        : "createPayrollRun";

      const response = await executeAnyMutation(
        mutationName,
        mutationInput,
        "createModel",
        null,
        base_url,
      );

      if (response.results) {
        props.onClose?.();
        props.onAction?.();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: props.isEditing
            ? "Payroll run updated."
            : "Payroll run created.",
        });
      } else {
        const errorMsg = JSON.parse(response.error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            errorMsg.Error ||
            "An error occurred while processing the payroll run.",
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={populatedFields}
      steps={[{ fields: payrollRunFields.map((f) => f.name) }]}
      onSubmit={handleSubmit}
      title={props.isEditing ? "Update Payroll Run" : "Create Payroll Run"}
      subtitle="Select payroll month → start & end dates auto-filled"
    />
  );
};

export default PayrollRunCreationForm;
